import { Colors, Icon, SelectOutlined } from '@amelio/ui-library';

import { InfoTooltip } from './InfoTooltip';

import type { CalibrationPerformanceLevel } from '@/types/calibration';
import type { OptionProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

/** Sentinel option value standing for "keep the level the manager gave". The report's own
 * contract has no level here — `calibratedPerformanceLevelId` is simply `null`. */
export const NO_LEVEL_CHANGE = 'no-change';

export interface LevelSelectProps {
    levels: CalibrationPerformanceLevel[];
    /** `null` while the row keeps the manager's own level. */
    value: string | null;
    onChange: (levelId: string | null) => void;
    ariaLabel: string;
    dataTestId?: string;
}

/**
 * Calibrated-level picker: "no change" until someone overrides the level the manager gave, tinted
 * the moment they do so an overridden row is visible at a glance down the column.
 *
 * Each level option carries a small dot in its own `color` — the company-chosen hex the report
 * DTO carries on `CalibrationPerformanceLevel`, applied as an inline style since it is not one of
 * the design system's color tokens (documented exception).
 */
export const LevelSelect = ({
    levels,
    value,
    onChange,
    ariaLabel,
    dataTestId,
}: LevelSelectProps) => {
    const { t } = useAmelioTranslations();

    const isOverridden = value !== null;

    const options: OptionProps[] = [
        { value: NO_LEVEL_CHANGE, label: t('calibration.table.noChange') },
        ...levels.map((level) => ({
            value: level.id,
            label: getLocalizedText(level.label),
            icon: (
                <span
                    aria-hidden
                    className={'h-2.5 w-2.5 rounded-full'}
                    style={{ backgroundColor: level.color }}
                />
            ),
        })),
    ];

    return (
        <div className={'flex items-center gap-2'}>
            <div className={'min-w-0 flex-1'}>
                <SelectOutlined
                    options={options}
                    value={value ?? NO_LEVEL_CHANGE}
                    onChange={(selected) =>
                        onChange(selected === NO_LEVEL_CHANGE ? null : selected)
                    }
                    size={'sm'}
                    sort={'none'}
                    triggerBackgroundClassName={isOverridden ? 'bg-primary-50' : 'bg-white'}
                    ariaLabel={ariaLabel}
                    dataTestId={dataTestId}
                />
            </div>

            {/* Beside the trigger rather than inside it: the library's `selectedIcon` marks the
                chosen option in the dropdown, not the closed trigger. */}
            {isOverridden && (
                <InfoTooltip
                    message={t('calibration.table.levelChanged')}
                    dataTestId={`${dataTestId}-changed`}
                >
                    <Icon
                        name={'essential-info-circle'}
                        size={'sm'}
                        color={Colors.PRIMARY}
                        ariaLabel={t('calibration.table.levelChanged')}
                    />
                </InfoTooltip>
            )}
        </div>
    );
};
